import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
  ArrowRightIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { useForgotPasswordMutation } from '../../services/authApi';

const ForgotPassword = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [forgotPassword, { isLoading, error }] = useForgotPasswordMutation();
  const [emailSent, setEmailSent] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const result = await forgotPassword(values.email).unwrap();
        setEmailSent(true);
      } catch (error) {
        console.error('Forgot password failed:', error);
        // Error will be displayed via the error state from the mutation
      }
    },
  });

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 animate-fade-in">
        <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="text-center animate-fade-in-up">
            {/* Logo */}
            <div className="flex justify-center mb-2">
              <img
                src="/assets/logo.png"
                alt="Innovative HRMS Logo"
                className="h-12 w-auto sm:h-16 md:h-20 lg:h-36 object-contain"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <div className="hidden text-2xl sm:text-3xl md:text-4xl font-bold text-primary-600">
                Innovative HRMS
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <CheckCircleIcon className="h-16 w-16 text-success-500" />
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-secondary-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm sm:text-base text-secondary-600 px-4">
              We've sent a password reset link to your email address.
            </p>
            <p className="mt-1 text-sm text-secondary-500 px-4">
              If you don't see the email in your inbox, please check your spam folder.
            </p>
          </div>

          {/* Success Card */}
          <div className="card card-glass animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            <div className="card-body p-4 sm:p-6 md:p-8">
              <div className="space-y-4 sm:space-y-6">
                <button
                  onClick={() => navigate('/login')}
                  className="btn-primary w-full py-3 sm:py-3.5 text-sm sm:text-base font-medium group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    Back to Login
                    <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </span>
                  <div className="absolute inset-0 bg-primary-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>

                <button
                  onClick={() => {
                    setEmailSent(false);
                    formik.resetForm();
                  }}
                  className="btn-secondary w-full py-3 sm:py-3.5 text-sm sm:text-base font-medium"
                >
                  Try Different Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 animate-fade-in">
      <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center animate-fade-in-up">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <img
              src="/assets/logo.png"
              alt="Innovative HRMS Logo"
              className="h-12 w-auto sm:h-16 md:h-20 lg:h-36 object-contain"
              onError={(e) => {
                // Fallback to text logo if image fails to load
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <div className="hidden text-2xl sm:text-3xl md:text-4xl font-bold text-primary-600">
              Innovative HRMS
            </div>
          </div>

          <h2 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-display font-bold text-secondary-900">
            Forgot Your Password?
          </h2>
          <p className="mt-2 text-sm sm:text-base text-secondary-600 px-4">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Forgot Password Form */}
        <div className="card card-glass animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          <div className="card-body p-4 sm:p-6 md:p-8">
            <form className="space-y-4 sm:space-y-6" onSubmit={formik.handleSubmit}>
              {/* Error Message */}
              {error && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-4 animate-fade-in">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-error-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-error-800">{error?.data?.message || 'Failed to send reset email. Please try again.'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.3s'}}>
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`form-input pl-10 ${
                      formik.touched.email && formik.errors.email
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder="Enter your email address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="form-error">{formik.errors.email}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 sm:py-3.5 text-sm sm:text-base font-medium group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-primary-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>
              </div>

              {/* Back to Login */}
              <div className="text-center animate-fade-in-up" style={{animationDelay: '0.5s'}}>
                <p className="text-sm text-secondary-600">
                  Remember your password?{' '}
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center animate-fade-in-up" style={{animationDelay: '0.6s'}}>
          <p className="text-sm text-secondary-600">
            © {new Date().getFullYear()} Innovative HRMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;